import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { StoreProvider } from "./context/StoreContext";
import { Navbar, Footer } from "./components/Navbar";
import { PromoBanner } from "./components/PromoBanner";
import { IntroSplash } from "./components/IntroSplash";
import Home from "./pages/Home";
import ResultsPage from "./pages/ResultsPage";
import CoursePage from "./pages/CoursePage";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import CartPage from "./pages/CartPage";
import MyLearning from "./pages/MyLearning";
import PlayerPage from "./pages/PlayerPage";
import AccountPage from "./pages/AccountPage";
import CertificatePage from "./pages/CertificatePage";
import InstructorPage from "./pages/InstructorPage";
import LegalPage from "./pages/LegalPage";
import { PaymentSuccess, PaymentCancel, PaypalReturn } from "./pages/PaymentResult";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCourses from "./pages/admin/AdminCourses";
import AdminMedia from "./pages/admin/AdminMedia";
import AdminStorage from "./pages/admin/AdminStorage";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminQA from "./pages/admin/AdminQA";
import AdminBundles from "./pages/admin/AdminBundles";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminRefunds from "./pages/admin/AdminRefunds";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading || user === null) return <p className="p-10 text-[var(--forge-dim)]">Loading…</p>;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
};

function Shell() {
  const location = useLocation();
  // Read hash from useLocation (reactive) — session_id means we're returning from Google OAuth
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <Navbar />
      <IntroSplash />
      <PromoBanner />
      <main className="min-h-[70vh]">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/courses" element={<ResultsPage />} />
          <Route path="/course/:id" element={<CoursePage />} />
          <Route path="/instructor" element={<InstructorPage />} />
          <Route path="/terms" element={<LegalPage />} />
          <Route path="/privacy" element={<LegalPage />} />
          <Route path="/refund-policy" element={<LegalPage />} />
          <Route path="/impressum" element={<LegalPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/reset-password" element={<AuthPage />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/payment/paypal" element={<PaypalReturn />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/wishlist" element={<CartPage wishlistMode />} />
          <Route path="/my-learning" element={<Protected><MyLearning /></Protected>} />
          <Route path="/learn/:id" element={<Protected><PlayerPage /></Protected>} />
          <Route path="/account" element={<Protected><AccountPage /></Protected>} />
          <Route path="/certificate/:id" element={<Protected><CertificatePage /></Protected>} />
          <Route path="/cert/:id" element={<CertificatePage publicView />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="courses" element={<AdminCourses />} />
            <Route path="media" element={<AdminMedia />} />
            <Route path="storage" element={<AdminStorage />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="bundles" element={<AdminBundles />} />
            <Route path="qa" element={<AdminQA />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="refunds" element={<AdminRefunds />} />
          </Route>
        </Routes>
      </main>
      <Footer />
    </>
  );
}

function App() {
  useEffect(() => {
    const h = (e) => {
      document.documentElement.style.setProperty("--mx", e.clientX + "px");
      document.documentElement.style.setProperty("--my", e.clientY + "px");
    };
    const flare = (e) => {
      const n = 9;
      for (let i = 0; i < n; i++) {
        const d = document.createElement("div");
        d.className = "click-spark";
        const ang = (i / n) * Math.PI * 2 + Math.random() * 0.7;
        const dist = 22 + Math.random() * 30;
        d.style.left = e.clientX + "px";
        d.style.top = e.clientY + "px";
        d.style.setProperty("--dx", Math.cos(ang) * dist + "px");
        d.style.setProperty("--dy", Math.sin(ang) * dist + "px");
        d.style.background = Math.random() > 0.5 ? "#38b6ff" : "#8fe6ff";
        document.body.appendChild(d);
        setTimeout(() => d.remove(), 620);
      }
    };
    document.addEventListener("mousemove", h);
    document.addEventListener("pointerdown", flare);
    return () => { document.removeEventListener("mousemove", h); document.removeEventListener("pointerdown", flare); };
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <StoreProvider>
          <div id="spotlight" />
          <Toaster position="bottom-center" theme="dark" richColors />
          <Shell />
        </StoreProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
